import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Snackbar,
    Tooltip,
    Typography
} from "@mui/material";
import LockIcon from "@mui/icons-material/LockOutlined";
import { useGetSyncFieldsQuery, useUpdateSyncFieldsMutation } from "../store/services/tenancy.service";

const MANDATORY_TOOLTIP = "Sync needs this field to identify and place users, so it cannot be turned off.";

/**
 * BD-871: lets a tenancy admin choose which fields the directory sync is allowed to write.
 * Unticking a field leaves whatever is already stored in Boxphish alone, so a value entered
 * by hand is not overwritten by the next sync.
 *
 * The field list comes from the API rather than being hardcoded, because it depends on the
 * tenancy's provider - Google Workspace does not supply UPN, Company Name, City, State or Country.
 */
export default function CustomiseImportFieldsDialog({ tenancyId, open, onClose }) {
    const { data, isLoading, isError } = useGetSyncFieldsQuery({ tenancyId }, { skip: !tenancyId || !open });
    const [updateSyncFields, { isLoading: isSaving }] = useUpdateSyncFieldsMutation();

    const [enabledByKey, setEnabledByKey] = useState({});
    const [snackbar, setSnackbar] = useState(null);

    useEffect(() => {
        if (data?.fields) {
            setEnabledByKey(
                data.fields.reduce((acc, field) => ({ ...acc, [field.key]: field.enabled }), {})
            );
        }
    }, [data]);

    const toggle = (key) => {
        setEnabledByKey((previous) => ({ ...previous, [key]: !previous[key] }));
    };

    const handleSave = () => {
        const disabledFields = (data?.fields ?? [])
            .filter((field) => !field.mandatory && !enabledByKey[field.key])
            .map((field) => field.key);

        updateSyncFields({ tenancyId, disabledFields })
            .unwrap()
            .then(() => {
                setSnackbar({ severity: "success", message: "Import fields updated. This applies from the next sync." });
                onClose();
            })
            .catch((error) => {
                console.error("Error updating import fields:", error);
                setSnackbar({
                    severity: "error",
                    message: typeof error === "string" ? error : "Failed to update import fields"
                });
            });
    };

    const fields = data?.fields ?? [];
    const optionalFields = fields.filter((field) => !field.mandatory);
    const mandatoryFields = fields.filter((field) => field.mandatory);
    const isMixedProvider = data?.provider === "mixed";

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>Customise Import Fields</DialogTitle>
                <DialogContent dividers>
                    {isLoading && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {isError && (
                        <Alert severity="error">Could not load the field list. Close this and try again.</Alert>
                    )}

                    {!isLoading && !isError && (
                        <>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Untick a field to stop the directory sync writing it. Values already in Boxphish are
                                kept, so anything you have entered by hand stays as it is. Changes apply from the next
                                sync.
                            </Typography>

                            {isMixedProvider && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    This tenancy syncs from both Microsoft Entra and Google Workspace. Fields marked
                                    Microsoft only are not supplied by Google Workspace.
                                </Alert>
                            )}

                            {optionalFields.map((field) => (
                                <Box key={field.key} sx={{ display: "block" }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={Boolean(enabledByKey[field.key])}
                                                onChange={() => toggle(field.key)}
                                                disabled={isSaving}
                                            />
                                        }
                                        label={
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <span>{field.displayName}</span>
                                                {isMixedProvider && field.microsoftOnly && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Microsoft only
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                    />
                                </Box>
                            ))}

                            {mandatoryFields.length > 0 && (
                                <>
                                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                        Always synced
                                    </Typography>
                                    {mandatoryFields.map((field) => (
                                        <Box key={field.key} sx={{ display: "block" }}>
                                            <Tooltip title={MANDATORY_TOOLTIP} placement="right">
                                                <FormControlLabel
                                                    control={<Checkbox checked disabled />}
                                                    label={
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                            <span>{field.displayName}</span>
                                                            <LockIcon
                                                                sx={{ fontSize: 14, color: "text.secondary" }}
                                                            />
                                                            {isMixedProvider && field.microsoftOnly && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Microsoft only
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                            </Tooltip>
                                        </Box>
                                    ))}
                                </>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} color="secondary" disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={isLoading || isError || isSaving}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={Boolean(snackbar)}
                autoHideDuration={5000}
                onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                {snackbar ? (
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ width: "100%" }}>
                        {snackbar.message}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </>
    );
}

CustomiseImportFieldsDialog.propTypes = {
    tenancyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired
};
